import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { useAlerts } from 'hooks/ui';
import { ApiError } from 'features/invite-users/useProjectInvitations';
import { useProjectFreeze } from './useProjectFreeze';
import { Button } from 'features/components/Button';
import { Dialog } from 'features/entry-form-v2/component/Dialog';

interface Props {
	projectId: string;
	projectName: string;
	onClose: () => void;
	visible: boolean;
}

export function FreezeProjectModal({ projectId, projectName, onClose, visible }: Props) {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const { setNotification, setError } = useAlerts();

	const { freezeProject, isFreezing } = useProjectFreeze();

	const handleSubmit = async () => {
		try {
			await freezeProject({ projectId: parseInt(projectId, 10) });
			setNotification({ message: dictionary.freezeSuccess });
			onClose();
		} catch (error) {
			const apiError = error as ApiError;
			setError({
				message: apiError?.message || dictionary.freezeFail
			});
		}
	};

	return (
		<Dialog open={visible} onClose={onClose} title={dictionary.modalTitle} hideCloseButton>
			<div className="space-y-6 w-[512px]">
				<p className="text-base text-gray-700 bg-gray-50 p-4 rounded-lg space-y-4">
					{dictionary.descriptionText}{' '}
					<span className="font-medium text-gray-900">{projectName}</span>
				</p>
				<div className="flex flex-wrap justify-end gap-3 pt-4 border-t-2 border-gray-200">
					<Button
						text={dictionary.cancelButton}
						onClick={onClose}
						variant="secondary"
						disabled={isFreezing}
					/>
					<Button
						text={dictionary.freezeButton}
						onClick={handleSubmit}
						variant="primary"
						disabled={isFreezing}
						loading={isFreezing}
					/>
				</div>
			</div>
		</Dialog>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		modalTitle: 'Freeze Project',
		descriptionText: 'You are about to freeze project:',
		cancelButton: 'Cancel',
		freezeButton: 'Freeze Project',
		freezeSuccess: 'Project frozen successfully.',
		freezeFail: 'Failed to freeze project. Please try again.',
		somethingWentWrong: 'Something went wrong. Please try again.'
	},
	[LanguageType.Norwegian]: {
		modalTitle: 'Frys prosjekt',
		descriptionText: 'Du er i ferd med å fryse prosjektet:',
		cancelButton: 'Avbryt',
		freezeButton: 'Frys prosjekt',
		freezeSuccess: 'Prosjektet er frosset.',
		freezeFail: 'Kunne ikke fryse prosjektet. Vennligst prøv igjen.',
		somethingWentWrong: 'Noe gikk galt. Vennligst prøv igjen.'
	}
} as const;
